<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  column: { type: Object, required: true },
  targetColumns: { type: Array, required: true },
  cardCount: { type: Number, default: 0 },
});

const emit = defineEmits(['confirm', 'cancel']);
const { t } = useI18n();

// 'target' = escolher destino dos cards | 'confirm' = confirmar exclusão
const step = ref('target');
const targetColumnId = ref(null);

const targetColumn = computed(() =>
  props.targetColumns.find(c => c.id === targetColumnId.value)
);

// sem cards não há o que mover — vai direto para a confirmação
const needsTarget = computed(() => props.cardCount > 0);

const canAdvance = computed(() => !needsTarget.value || !!targetColumn.value);

function goToConfirm() {
  if (!canAdvance.value) return;
  step.value = 'confirm';
}

function onConfirm() {
  emit('confirm', needsTarget.value ? targetColumnId.value : null);
}
</script>

<template>
  <div
    class="fixed inset-0 z-[10010] flex items-center justify-center bg-black/40"
    @click.self="emit('cancel')"
  >
    <div
      class="bg-n-background rounded-xl shadow-2xl w-full max-w-sm mx-4 p-5 flex flex-col gap-4"
    >
      <!-- Etapa 1: destino dos cards -->
      <template v-if="step === 'target'">
        <div class="flex flex-col gap-1">
          <h3 class="text-sm font-semibold text-n-slate-12">
            {{ t('KANBAN.SETTINGS.DELETE_COLUMN_MOVE_TITLE') }}
          </h3>
          <p class="text-xs text-n-slate-10">
            {{
              needsTarget
                ? t('KANBAN.SETTINGS.DELETE_COLUMN_MOVE_DESC', {
                    count: cardCount,
                    name: column.name,
                  })
                : t('KANBAN.SETTINGS.DELETE_COLUMN_EMPTY_DESC', {
                    name: column.name,
                  })
            }}
          </p>
        </div>

        <div v-if="needsTarget" class="flex flex-col gap-1.5">
          <button
            v-for="col in targetColumns"
            :key="col.id"
            type="button"
            class="flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-left transition-colors border"
            :class="
              targetColumnId === col.id
                ? 'text-n-brand font-medium bg-n-alpha-1 border-n-brand'
                : 'text-n-slate-12 border-n-weak hover:bg-n-alpha-1'
            "
            @click="targetColumnId = col.id"
          >
            <span class="truncate">{{ col.name }}</span>
            <span
              v-if="targetColumnId === col.id"
              class="i-lucide-check size-3 text-n-brand ml-auto flex-shrink-0"
            />
          </button>
        </div>

        <div class="flex justify-end gap-2 pt-1">
          <Button
            faded
            slate
            sm
            :label="t('KANBAN.FOLDERS.CANCEL')"
            type="button"
            @click="emit('cancel')"
          />
          <Button
            sm
            :label="t('KANBAN.SETTINGS.DELETE_COLUMN_CONTINUE')"
            :disabled="!canAdvance"
            type="button"
            @click="goToConfirm"
          />
        </div>
      </template>

      <!-- Etapa 2: confirmação da exclusão -->
      <template v-else>
        <div class="flex flex-col gap-1">
          <h3 class="text-sm font-semibold text-n-slate-12">
            {{ t('KANBAN.SETTINGS.DELETE_COLUMN_CONFIRM_TITLE') }}
          </h3>
          <p class="text-xs text-n-slate-10">
            {{
              needsTarget
                ? t('KANBAN.SETTINGS.DELETE_COLUMN_CONFIRM_DESC', {
                    count: cardCount,
                    name: column.name,
                    target: targetColumn?.name,
                  })
                : t('KANBAN.SETTINGS.DELETE_COLUMN_CONFIRM_EMPTY_DESC', {
                    name: column.name,
                  })
            }}
          </p>
        </div>

        <div class="flex justify-end gap-2 pt-1">
          <Button
            faded
            slate
            sm
            :label="t('KANBAN.SETTINGS.DELETE_COLUMN_BACK')"
            type="button"
            @click="step = 'target'"
          />
          <Button
            sm
            ruby
            :label="t('KANBAN.SETTINGS.DELETE_COLUMN_CONFIRM_BTN')"
            type="button"
            @click="onConfirm"
          />
        </div>
      </template>
    </div>
  </div>
</template>
